import { inject, Injectable, InjectionToken } from '@angular/core';
import { environment } from '@environments/environment';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { Time } from '@shared/constants';
import { DataService } from '@shared/services';
import { CurrentLanguageService } from '@shared/services/language/current-language.service';
import { LogsService } from '@shared/services/logs';
import { PlatformContextService } from '@shared/services/platform-context';
import {
  catchError,
  defer,
  EMPTY,
  filter,
  first,
  from,
  map,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  take,
  tap,
  throwError,
  timeout,
} from 'rxjs';

type ImportableLib = 'maps' | 'places';

type LibReturnMap = {
  maps: google.maps.MapsLibrary;
  places: google.maps.PlacesLibrary;
};

function importLib<T extends ImportableLib>(name: T): Promise<LibReturnMap[T]> {
  return importLibrary(name);
}

// Provided so a spec replaces it through TestBed: vi.mock misses the loader once another spec of its worker loaded it
export const GOOGLE_MAPS_SET_OPTIONS = new InjectionToken<typeof setOptions>('GOOGLE_MAPS_SET_OPTIONS', {
  factory: () => setOptions,
});

/**
 * Service for managing interaction with the Google Maps and Places APIs, including library loading,
 * API access, and resource caching. Automatically handles initialization and error logging.
 *
 * This service:
 * - Loads the Google Maps and Places libraries only when needed.
 * - Provides utilities for waiting for the APIs to load.
 * - Fetches and caches geographical data associated with Google Place IDs.
 * - Ensures library reuse and error handling when interacting with external APIs.
 *
 * @remarks
 * - Uses `ReplaySubject` to track the loading state of the Google Maps API.
 * - Relies on `DataService` for cache operations and `LogsService` for structured logging.
 * - Designed with lazy loading and caching to minimize network and API usage.
 * - Follows reactive programming principles for better composability in Angular applications.
 *
 * @example
 * ```typescript
 * const googleService = inject(GoogleService);
 * googleService.waitForApiLoad().subscribe(() => {
 *   console.log('Google Maps API loaded successfully');
 * });
 * ```
 *
 * @see {@link https://developers.google.com/maps/documentation | Google Maps API Documentation}
 */
@Injectable({ providedIn: 'root' })
export class GoogleService {
  private readonly apiGoogleMapLoaded = new ReplaySubject<boolean>(1);
  private readonly dataService = inject(DataService);
  private readonly logs = inject(LogsService);
  private readonly isServer = inject(PlatformContextService).isServer;

  private mapsLib?: Promise<google.maps.MapsLibrary>;
  private placesLib?: Promise<google.maps.PlacesLibrary>;

  constructor() {
    if (this.isServer()) return;
    const { language, region } = inject(CurrentLanguageService);
    inject(GOOGLE_MAPS_SET_OPTIONS)({
      key: environment.integrations?.googleMapsApi?.apiKey ?? '',
      v: 'weekly',
      language: language.locale,
      region: region.region,
    });
  }

  /**
   * Waits for the Google Maps API to load by subscribing to an internal observable.
   * Emits and completes successfully when the API is loaded, or throws an error if a timeout occurs.
   *
   * @param timeoutMs The maximum time, in milliseconds, to wait for the API to load. Defaults to 10,000ms (10 seconds).
   * @return An `Observable<void>` that emits and completes when the API is loaded, or errors if the timeout is exceeded.
   * @throws Throws an `Error` if the API fails to load within the provided timeout.
   */
  waitForApiLoad(timeoutMs = 10_000): Observable<void> {
    if (this.isServer()) return of(void 0);
    return defer(() => {
      this.loadMaps();
      return this.apiGoogleMapLoaded;
    }).pipe(
      filter(Boolean),
      first(),
      timeout({
        each: timeoutMs,
        with: () => {
          throw new Error('Timeout waiting for Google Maps API to load');
        },
      }),
      map(() => undefined)
    );
  }

  /**
   * Retrieves geographical coordinates (latitude and longitude) for a given Google Place ID,
   * leveraging cache when available and fetching from the Google Places API otherwise.
   *
   * @param id - The unique identifier of the Google Place.
   * @returns An `Observable` emitting the coordinates as a `google.maps.LatLngLiteral` (with `lat` and `lng` properties)
   *          or `null` if the place has no associated coordinates or an error occurs.
   * @throws Will catch and log errors if the fetch process fails.
   * @remarks
   * - Caches fetched coordinates for future use.
   * - Accesses Google Places API only when the requested ID is not in the cache.
   * - Side effect: Updates internal cache with fetched coordinates.
   * @example
   * ```typescript
   * const placeId = 'ChIJN1t_tDeuEmsRUsoyG83frY4';
   * this.fetchCoordinatesByGooglePlaceId(placeId).subscribe((coords) => {
   *   if (coords) {
   *     console.log(`Latitude: ${coords.lat}, Longitude: ${coords.lng}`);
   *   } else {
   *     console.log('No coordinates found.');
   *   }
   * });
   * ```
   */
  fetchCoordinatesByGooglePlaceId(id: string): Observable<google.maps.LatLngLiteral | null> {
    if (!id) return of(null);
    if (this.isServer()) return of(null);

    return this.dataService.get$(`google_place_coordinates::${id}`, 'CACHE').pipe(
      take(1),
      switchMap((cache) => {
        if (cache) {
          this.logs.debug('GoogleService', `[CACHE] Coordinates for Place ID "${id}" retrieved from cache`, {
            muted: true,
          });
          return of(cache as google.maps.LatLngLiteral | null);
        }

        return this.ensurePlaces().pipe(
          switchMap((placesLib) => {
            const place = new placesLib.Place({ id, requestedLanguage: 'fr' });

            return from(place.fetchFields({ fields: ['location'] })).pipe(
              map((): google.maps.LatLngLiteral | null => {
                if (place.location) {
                  return { lat: place.location.lat(), lng: place.location.lng() };
                }
                return null;
              }),
              tap((coordinates) => {
                this.dataService
                  .set(`google_place_coordinates::${id}`, coordinates, Time.UNLIMITED, 'CACHE')
                  .pipe(take(1))
                  .subscribe();
              }),
              catchError((error) => {
                this.logs.error('GoogleService', 'Error fetching coordinates', error);
                return of(null);
              })
            );
          })
        );
      })
    );
  }

  /**
   * Ensures that the Google Maps library is loaded and provides an Observable for accessing it.
   * If the library is already loaded, it reuses the existing instance.
   *
   * @returns {Observable<google.maps.MapsLibrary>} An Observable that emits the loaded Google Maps library.
   * @remarks This method uses lazy loading and caching to optimize library initialization.
   */
  private loadMaps(): void {
    if (this.mapsLib) return;
    this.ensureMaps()
      .pipe(
        take(1),
        tap(() => {
          this.apiGoogleMapLoaded.next(true);
          this.logs.info('GoogleService', 'Google Maps API loaded successfully');
        }),
        catchError((error) => {
          this.apiGoogleMapLoaded.next(false);
          this.logs.error('GoogleService', 'Error loading Google Maps API', error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  private ensureMaps(): Observable<google.maps.MapsLibrary> {
    if (this.isServer()) return throwError(() => new Error('Maps library unavailable on server'));
    const promise = this.mapsLib ?? (this.mapsLib = importLib('maps')); // setOptions() a déjà installé l’infra
    return defer(() => from(promise));
  }

  /**
   * Ensures that the Google Maps Places Library is loaded and returns it as an Observable.
   *
   * @returns An `Observable` that emits the `google.maps.PlacesLibrary` instance once it is loaded.
   * @remarks
   * If the library is already cached, it is reused. Otherwise, dynamically imports the library.
   * Helps manage asynchronous loading of external libraries in a reactive context.
   */
  private ensurePlaces(): Observable<google.maps.PlacesLibrary> {
    if (this.isServer()) return throwError(() => new Error('Places library unavailable on server'));
    const promise = this.placesLib ?? (this.placesLib = importLib('places'));
    return defer(() => from(promise));
  }
}
